import React, { useEffect, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { store, persistor, RootState } from '@/store';
import AppNavigator from '@/navigation/AppNavigator';
import LoadingScreen from '@/components/common/LoadingScreen';
import { initializeAuth } from '@/store/slices/authSlice';
import {
  configureNotificationHandler,
  registerForPushNotifications,
  registerNotificationListeners,
} from '@/services/pushNotifications';
import { replayPendingRoute } from '@/services/notificationRouter';

/**
 * Web entry — mounts the native AppNavigator through react-native-web so
 * the actual TimeClock / Schedule / Time Off screens render in the
 * browser. Iteration surface for Linux WorkSpaces where no mobile
 * emulator is practical.
 *
 * Fonts are loaded via CSS in web/index.html (Google Fonts CDN) — we
 * deliberately skip expo-font here because expo-modules-core uses TS-only
 * syntax that webpack's babel pipeline doesn't process. Native-only
 * modules (NFC, geolocation, …) are aliased to web stubs in
 * web/webpack.config.js.
 */
const AppContent: React.FC = () => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  // Latest auth value, readable from the notification listener registered once.
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  useEffect(() => {
    store.dispatch(initializeAuth());
    // Web Push presentation is handled by the service worker; this is a no-op
    // kept for parity. Wire the notification-tap listeners (SW message + the
    // cold-start ?push= param).
    configureNotificationHandler();
    const unsubscribe = registerNotificationListeners(() => isAuthenticatedRef.current);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // Subscribe to Web Push now that a JWT is available, and replay any tap
      // that arrived before authentication.
      registerForPushNotifications();
      replayPendingRoute(true);
    }
  }, [isAuthenticated]);

  // On a wide (desktop/tablet) viewport the phone-first layout would stretch
  // edge-to-edge and look broken. Constrain the whole app to a phone-width
  // column centered on a neutral backdrop; on a real phone (<= 480px) this is
  // a no-op (full width) so nothing changes for the native-feel case.
  return (
    <SafeAreaProvider>
      <View style={styles.backdrop}>
        <View style={styles.appColumn}>
          <AppNavigator />
        </View>
      </View>
    </SafeAreaProvider>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    width: '100%',
    backgroundColor: '#e9edf2',
    alignItems: 'center',
  },
  appColumn: {
    flex: 1,
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#ffffff',
    // Subtle side separation from the backdrop on wide screens.
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
  },
});

const App: React.FC = () => (
  <Provider store={store}>
    <PersistGate loading={<LoadingScreen />} persistor={persistor}>
      <AppContent />
    </PersistGate>
  </Provider>
);

export default App;
